//   overhead coef  what the structure costs, as a share of the dry cost
//   sale coef      what the cost price is multiplied by to get the price
//   K coef         their product — the trade's "coefficient de vente"
//
//        K = (1 + overhead coef) × sale coef
//        selling price = purchase price × K

export const parseCoef = value => {
  const number = parseFloat(String(value).replace(",", "."))
  return Number.isFinite(number) && number >= 0 ? number : 0
}

// Signed variant, for the overhead coefficient only: a negative value expresses
// a credit.
export const parseCoefSigned = value => {
  const number = parseFloat(String(value).replace(",", "."))
  return Number.isFinite(number) ? number : 0
}

// Looked up in that order: this quote → the category in Preferences → the
// global ratio.
export const overheadCoefFor = (state, category) => {
  const override = (state.overheadCoefOverrides || {})[category]
  if (override != null && override !== "") {
    const parsed = typeof override === "number" ? override : parseCoefSigned(override)
    if (Number.isFinite(parsed)) return parsed
  }

  const accountCoefs = state.account?.overhead_coefs || {}
  if (accountCoefs[category] != null) return parseCoef(accountCoefs[category])

  return parseCoef(state.account?.overhead_ratio) / 100
}

// The sale coefficient DECIDED for a category: this quote, then the category in
// Preferences. There is no global fallback here — the global setting is a K,
// read by defaultKFor.
export const policySaleCoefFor = (state, category) => {
  const stored = (state.saleCoefOverrides || {})[category]
  if (stored != null && stored !== "") {
    const parsed = typeof stored === "number" ? stored : parseCoef(stored)
    if (parsed > 0) return parsed
  }

  // A missing key means "nothing decided", not "a coefficient of zero".
  const fromAccount = (state.account?.sale_coefs || {})[category]
  if (fromAccount != null && fromAccount !== "") {
    const parsed = typeof fromAccount === "number" ? fromAccount : parseCoef(fromAccount)
    if (parsed > 0) return parsed
  }

  return null
}

export const saleCoefFromK = (overheadCoef, k) => {
  const divisor = 1 + overheadCoef
  return divisor > 0 ? k / divisor : 0
}

// The account's "sale price by coefficient" setting. It applies to the purchase
// price directly, so it is a K, not a coefficient on the cost price.
export const defaultKFor = state => {
  if (!state.allowMarkup) return null

  const markup = parseCoef(state.priceMarkupCoefficient)
  return markup > 0 ? markup : null
}

export const composeK = (overheadCoef, saleCoef) => (1 + overheadCoef) * saleCoef

export const effectiveSaleCoefFor = (state, category) => {
  const decided = policySaleCoefFor(state, category)
  if (decided != null) return decided

  const defaultK = defaultKFor(state)
  if (defaultK == null) return null

  return saleCoefFromK(overheadCoefFor(state, category), defaultK)
}

export const policyPriceFor = (state, category, purchasePrice) => {
  const purchase = parseFloat(purchasePrice) || 0
  if (purchase <= 0) return null

  const saleCoef = effectiveSaleCoefFor(state, category)
  if (saleCoef == null) return null

  return purchase * composeK(overheadCoefFor(state, category), saleCoef)
}

// A price coming from the policy must follow a purchase price correction, a
// negotiated price must never move. After a reload the `_pricedByPolicy` flag
// is gone, so the price is compared to what the policy would give.
export const isPolicyPriced = (state, node) => {
  if (node._pricedByPolicy === true)  return true
  if (node._pricedByPolicy === false) return false

  const expected = policyPriceFor(state, node.cost_category || "materials", node.purchase_price)
  if (expected == null) return false

  const current = parseFloat(node.unit_price) || 0
  return Math.abs(Math.round(expected * 100) / 100 - current) < 0.005
}
